import { Badge, Button, Group, Modal, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconTool } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { Money } from '../../components/Money';
import { QueryState } from '../../components/QueryState';
import { SectionCard } from '../../components/ui';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';

interface Row {
  id: string;
  number: string;
  at: string;
  client: string | null;
  address: string;
  company: string | null;
  reason: string | null;
  parts: string[];
  /** zamkniete albo wykonane (bez faktury — QA qa11050853) */
  status: string;
  charge: { totalGr: number; paidGr: number; invoiceState: string; invoiceError: string; linkSentAt: string | null } | null;
}

const DAY = 86_400_000;

/**
 * Serwis do zafakturowania (QA 11.2026 P7): płatne naprawy zamknięte bez numeru faktury — dla księgowości bez dostępu do modułu Serwis.
 * Fakturę wystawia się w Fakturowni, a tu wpisuje się jej numer (powiadomienie znika). Po wpłacie online fakturę wystawia CRM (docs/26).
 */
export function ServiceToInvoice({ highlight }: { highlight: string | null }) {
  const q = useQuery({ queryKey: ['service-to-invoice'], queryFn: () => api<Row[]>('/service-to-invoice') });
  const [row, setRow] = useState<Row | null>(null);
  if (!q.isLoading && !q.data?.length) return null;
  return (
    <SectionCard title="Serwis do zafakturowania" icon={<IconTool size={16} />} padding={0}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Zgłoszenie</Table.Th>
              <Table.Th>Klient i adres</Table.Th>
              <Table.Th>Części i powód</Table.Th>
              <Table.Th>Zamknięte</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(q.data ?? []).map((r) => {
              const days = Math.floor((Date.now() - Date.parse(r.at)) / DAY);
              return (
                <Table.Tr key={r.id} bg={r.id === highlight ? 'var(--mantine-color-yellow-light)' : undefined}>
                  <Table.Td>
                    <Text fw={600}>{r.number}</Text>
                    {r.company && (
                      <Text size="xs" c="dimmed">
                        firma {r.company}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{r.client ?? '—'}</Text>
                    <Text size="xs" c="dimmed">
                      {r.address}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{r.parts.length ? r.parts.join('; ') : 'bez części w wizytach'}</Text>
                    {r.charge && (
                      <Text size="xs" c={r.charge.invoiceState === 'blad' ? 'red.7' : 'dimmed'}>
                        do zapłaty <Money gr={r.charge.totalGr} /> brutto
                        {r.charge.paidGr > 0 ? <> · zapłacono online <Money gr={r.charge.paidGr} /></> : r.charge.linkSentAt ? ` · link do zapłaty wysłany ${formatDateTime(r.charge.linkSentAt)}` : ' · link do zapłaty niewysłany'}
                        {r.status === 'wykonane' ? ' · zgłoszenie wykonane, niezamknięte' : ''}
                        {r.charge.invoiceState === 'blad' ? ` · faktura nie wystawiła się automatycznie: ${r.charge.invoiceError}` : ''}
                      </Text>
                    )}
                    {r.reason && (
                      <Text size="xs" c="dimmed">
                        powód: {r.reason}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm">{formatDateTime(r.at)}</Text>
                      {days >= 7 && (
                        <Badge size="xs" color="red" variant="light">
                          {days} dni
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Button size="compact-xs" variant="light" onClick={() => setRow(r)}>
                      Wpisz nr faktury
                    </Button>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </QueryState>
      {row && <InvoicedModal row={row} onClose={() => setRow(null)} />}
    </SectionCard>
  );
}

function InvoicedModal({ row, onClose }: { row: Row; onClose: () => void }) {
  const qc = useQueryClient();
  const [nr, setNr] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/service-tickets/${row.id}/invoiced`, { method: 'POST', body: { invoiceNumber: nr.trim() } }),
    onSuccess: () => {
      notifyOk(`Zapisano fakturę za serwis ${row.number}`);
      void qc.invalidateQueries({ queryKey: ['service-to-invoice'] });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Faktura za serwis ${row.number}`}>
      <Stack>
        <Text size="sm" c="dimmed">
          Wystaw fakturę w Fakturowni i wpisz jej numer — zgłoszenie zniknie z listy, a powiadomienie z dzwonka.
        </Text>
        <TextInput label="Numer faktury" value={nr} onChange={(e) => setNr(e.currentTarget.value)} required data-autofocus />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!nr.trim()}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
